<script setup>
import { reactive, computed, ref } from 'vue';
import { ChevronUpIcon } from '@heroicons/vue/outline';
import QrcodeVue from 'qrcode.vue';
import ViceButton from '@/Components/Buttons/ViceButton.vue';

const emit = defineEmits(['close']);

const closeModal = () => {
  emit('close');
  setTimeout(() => {
    currentStep.value = steps.configuration;
  }, 200);
};

const steps = {
  configuration: 'configuration',
  qrCode: 'qrCode',
  success: 'success',
};

const currentStep = ref(steps.configuration);

const securityOptions = [
  { value: 'nopass', label: 'None' },
  { value: 'WPA', label: 'WPA / WPA2 / WPA3' },
  { value: 'WEP', label: 'WEP' },
  { value: 'WPA2ENTERPRISE', label: 'WPA2 enterprise' },
];

const eapMethodOptions = [
  { value: 'NONE', label: 'None' },
  { value: 'PEAP', label: 'PEAP' },
  { value: 'PWD', label: 'PWD' },
  { value: 'TLS', label: 'TLS' },
  { value: 'TTLS', label: 'TTLS' },
];

const phase2MethodOptions = [
  { value: 'NONE', label: 'None' },
  { value: 'GTC', label: 'GTC' },
  { value: 'MSCHAP', label: 'MSCHAP' },
  { value: 'MSCHAPV2', label: 'MSCHAPV2' },
  { value: 'PAP', label: 'PAP' },
];

const form = reactive({
  ssid: '',
  password: '',
  security: 'WPA',
  hidden: false,
  username: '',
  anonymousIdentity: '',
  eapMethod: 'NONE',
  phase2Method: 'NONE',
});

const advancedFieldsExpanded = ref(false);

const showPasswordField = computed(() => form.security !== 'NONE');
const showUsernameField = computed(() => form.security == 'WPA2ENTERPRISE');
const showAdvancedFields = computed(() => form.security == 'WPA2ENTERPRISE');

const canGoToNextStep = computed(() => form.ssid.length > 0);

const qrCodeValue = computed(() => {
  let wifiConfig = `WIFI:S:${form.ssid};T:${form.security};P:${form.password};H:${form.hidden};`;

  if (showUsernameField.value) {
    wifiConfig += `U:${form.username};A:${form.anonymousIdentity};E:${form.eapMethod};M:${form.phase2Method};`;
  }

  return wifiConfig;
});
</script>

<template>
  <div class="w-full">
    <div v-if="currentStep == steps.configuration">
      <h2 class="font-semibold pb-3">Connect to wifi</h2>
      <div class="w-full space-y-4">
        <!-- SSID -->
        <div class="w-full">
          <label for="ssid" class="block text-sm font-medium text-gray-700 mb-1">
            Network name / SSID
          </label>
          <input
            id="ssid"
            type="text"
            v-model="form.ssid"
            class="
              mt-1
              focus:ring-vice-teal-500
              focus:border-vice-teal-500
              block
              w-full
              shadow-sm
              sm:text-sm
              border-gray-300
              rounded-md
            "
          />
        </div>

        <!-- Encryption -->
        <div class="w-full">
          <div class="block text-sm font-medium text-gray-700 mb-1">Encryption</div>

          <div class="grid grid-cols-2 w-full gap-2">
            <div
              class="flex w-full items-center"
              v-for="securityOption in securityOptions"
              :key="securityOption.value"
            >
              <label
                :for="securityOption.value"
                :class="{
                  'border-vice-teal-400 bg-vice-teal-400': securityOption.value == form.security,
                  'border-gray-300': securityOption.value != form.security,
                }"
                class="
                  hover:bg-vice-teal-400
                  bg-opacity-5
                  hover:bg-opacity-5
                  border
                  rounded-md
                  w-full
                  p-2.5
                  py-1.5
                  flex
                  items-center
                  gap-2
                  cursor-pointer
                "
              >
                <input
                  :id="securityOption.value"
                  v-model="form.security"
                  :value="securityOption.value"
                  name="session_access"
                  type="radio"
                  class="
                    h-4
                    w-4
                    border-gray-300
                    text-vice-teal-400
                    outline-none
                    ring-0
                    focus:outline-none
                    focus:ring-0
                  "
                />
                <span class="block text-sm font-medium leading-6 text-gray-900">
                  {{ securityOption.label }}
                </span>
              </label>
            </div>
          </div>
        </div>

        <!-- Hidden network -->
        <div class="w-full">
          <label for="is-hidden" class="block text-sm font-medium text-gray-700 mb-1">
            Hidden network
          </label>
          <div class="flex items-center mr-4">
            <input
              v-model="form.hidden"
              id="is-hidden"
              type="checkbox"
              class="
                w-4
                h-4
                text-vice-teal-400
                border-gray-300
                rounded
                focus:ring-vice-teal-400 focus:ring-2
                cursor-pointer
              "
            />
            <label
              for="is-hidden"
              class="cursor-pointer ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
            >
              Is hidden
            </label>
          </div>
        </div>

        <!-- ALTERNATIVE OPTIONS -->

        <!-- username -->
        <div class="w-full" v-if="showUsernameField">
          <label for="username" class="block text-sm font-medium text-gray-700 mb-1">
            Username
          </label>
          <input
            id="username"
            type="text"
            v-model="form.username"
            class="
              mt-1
              focus:ring-vice-teal-500
              focus:border-vice-teal-500
              block
              w-full
              shadow-sm
              sm:text-sm
              border-gray-300
              rounded-md
            "
          />
        </div>

        <!-- Passwrod -->
        <div class="w-full" v-if="showPasswordField">
          <label for="password" class="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <input
            id="password"
            type="text"
            v-model="form.password"
            class="
              mt-1
              focus:ring-vice-teal-500
              focus:border-vice-teal-500
              block
              w-full
              shadow-sm
              sm:text-sm
              border-gray-300
              rounded-md
            "
          />
        </div>

        <div class="w-full space-y-4" v-if="showAdvancedFields">
          <div
            class="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1 cursor-pointer"
            @click="advancedFieldsExpanded = !advancedFieldsExpanded"
          >
            Advanced options
            <ChevronUpIcon
              class="w-4 transform"
              :class="{ 'rotate-180': !advancedFieldsExpanded }"
            ></ChevronUpIcon>
          </div>

          <div class="w-full space-y-4" v-if="advancedFieldsExpanded">
            <!-- username -->
            <div class="w-full">
              <label for="anonymous-identity" class="block text-sm font-medium text-gray-700 mb-1">
                Anonymous identity
              </label>
              <input
                id="anonymous-identity"
                type="text"
                v-model="form.anonymousIdentity"
                class="
                  mt-1
                  focus:ring-vice-teal-500
                  focus:border-vice-teal-500
                  block
                  w-full
                  shadow-sm
                  sm:text-sm
                  border-gray-300
                  rounded-md
                "
              />
            </div>

            <div class="w-full">
              <label for="eap-method" class="block text-sm font-medium text-gray-700 mb-1">
                EAP Method
              </label>
              <select
                id="eap-method"
                v-model="form.eapMethod"
                class="
                  mt-1
                  focus:ring-vice-teal-500
                  focus:border-vice-teal-500
                  block
                  w-full
                  shadow-sm
                  sm:text-sm
                  border-gray-300
                  rounded-md
                "
              >
                <option
                  v-for="eapMethodOption in eapMethodOptions"
                  :key="eapMethodOption.value"
                  :value="eapMethodOption.value"
                >
                  {{ eapMethodOption.label }}
                </option>
              </select>
            </div>

            <!-- phase2MethodOptions -->
            <div class="w-full">
              <label for="phase-2-method" class="block text-sm font-medium text-gray-700 mb-1">
                Phase 2 authentication
              </label>
              <select
                id="eap-method"
                v-model="form.phase2Method"
                class="
                  mt-1
                  focus:ring-vice-teal-500
                  focus:border-vice-teal-500
                  block
                  w-full
                  shadow-sm
                  sm:text-sm
                  border-gray-300
                  rounded-md
                "
              >
                <option
                  v-for="phase2MethodOption in phase2MethodOptions"
                  :key="phase2MethodOption.value"
                  :value="phase2MethodOption.value"
                >
                  {{ phase2MethodOption.label }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div class="flex items-center mt-6 gap-4 justify-end">
        <ViceButton
          data-cy="cancel-session-timezone"
          red
          class="sm:h-8 sm:w-24 sm:justify-center"
          @click="closeModal"
        >
          Cancel
        </ViceButton>
        <ViceButton
          teal
          :disabled="!canGoToNextStep"
          class="sm:h-8 sm:w-24 sm:justify-center"
          @click="currentStep = steps.qrCode"
        >
          Connect
        </ViceButton>
      </div>
    </div>

    <div v-if="currentStep == steps.qrCode">
      <h2 class="text-center font-semibold pb-8">
        Scan the QR code below with your smartglasses
        <br />
        to connect to WIFI network
      </h2>
      <qrcode-vue :value="qrCodeValue" size="250" level="L" renderAs="svg" class="mx-auto" />

      <div class="flex items-center mt-12 gap-4 justify-end">
        <ViceButton
          data-cy="cancel-session-timezone"
          red
          class="sm:h-8 sm:w-24 sm:justify-center"
          @click="currentStep = steps.configuration"
        >
          Back
        </ViceButton>
        <ViceButton
          data-cy="save-session-timezone"
          teal
          class="sm:h-8 sm:w-24 sm:justify-center"
          @click="closeModal"
        >
          Done
        </ViceButton>
      </div>
    </div>
  </div>
</template>
